import React, { useState, useEffect } from 'react';
import { getNotifications } from './dmsApi';
import { makeStyles } from '@mui/styles';
import { Box, Typography, CircularProgress } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#E8F0F5',
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    fontFamily: 'Montserrat, sans-serif',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#131C4C',
    marginBottom: '20px',
    fontFamily: 'Montserrat, sans-serif',
  },
  notificationItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    },
  },
  notificationText: {
    flex: 1,
    fontSize: '14px',
    color: '#333333',
    fontFamily: 'Montserrat, sans-serif',
    lineHeight: '1.5',
  },
  notificationTime: {
    fontSize: '12px',
    color: '#999999',
    marginTop: '4px',
    fontFamily: 'Montserrat, sans-serif',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666666',
    fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
  },
  errorMessage: {
    color: '#d32f2f',
    fontSize: '14px',
    textAlign: 'center',
    padding: '20px',
    fontFamily: 'Montserrat, sans-serif',
  },
}));

const Notifications = () => {
  const classes = useStyles();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchNotifications();
    // Set up polling to refresh notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      
      if (!userId) {
        setError('User ID not found. Please log in again.');
        setLoading(false);
        return;
      }

      const data = await getNotifications(userId);
      // Filter to show only unread notifications (if read status is available)
      // For now, show all notifications as the API doesn't specify read status
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatNotificationText = (notification) => {
    // Use the body from the API response
    if (notification.body) {
      return notification.body;
    }
    
    // Fallback formatting if body is not available
    if (notification.notification_type === 'assignment_notification') {
      return `Document ${notification.document_id} has been assigned to you by ${notification.action_by_name || 'Admin'}.`;
    } else if (notification.notification_type === 'document_updated_after_assignment') {
      return `Document ${notification.document_id} assigned to you was updated by ${notification.action_by_name || 'Admin'}.`;
    }
    
    return notification.title || 'New notification';
  };

  const getTimeAgo = (notification) => {
    if (notification.time_ago) {
      return notification.time_ago;
    }
    if (notification.timestamp) {
      // Simple time ago calculation if time_ago is not provided
      const now = new Date();
      const timestamp = new Date(notification.timestamp);
      const diffMs = now - timestamp;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
    return '';
  };

  if (loading && notifications.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.title}>Unread Notifications</Typography>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  if (error && notifications.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.title}>Unread Notifications</Typography>
        <Typography className={classes.errorMessage}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Unread Notifications</Typography>
      
      {notifications.length === 0 ? (
        <Box className={classes.emptyState}>
          No unread notifications
        </Box>
      ) : (
        notifications.map((notification, index) => (
          <Box key={notification.notification_id || index} className={classes.notificationItem}>
            <Typography className={classes.notificationText}>
              {formatNotificationText(notification)}
            </Typography>
            {getTimeAgo(notification) && (
              <Typography className={classes.notificationTime}>
                {getTimeAgo(notification)}
              </Typography>
            )}
          </Box>
        ))
      )}
    </Box>
  );
};

export default Notifications;

